import axios from 'axios';
import { useAuthStore } from '../store/authStore';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const api = axios.create({ baseURL: API_BASE_URL });

// Attach the access token to every request unless explicitly skipped.
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && !config.skipAuth) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single-flight refresh: many concurrent 401s share one refresh round-trip.
let refreshPromise = null;

const STORAGE_KEY = 'mdu-auth';

/** The session as last saved by any tab (tabs share it through localStorage). */
function savedSession() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null')?.state ?? null;
  } catch {
    return null;
  }
}

/**
 * A saved session is "ours" only when it's the same person in the same mode:
 * a super admin tab and a tab acting as a customer must never swap tokens.
 */
function sameSession(saved, store) {
  if (!saved?.user?.id || saved.user.id !== store.user?.id) return false;
  const savedAdmin = saved.impersonation?.adminInfo ?? null;
  const ourAdmin = store.impersonation?.adminInfo ?? null;
  return JSON.stringify(savedAdmin) === JSON.stringify(ourAdmin);
}

/** True while an access token has at least 30 seconds left. */
export function isTokenFresh(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() + 30_000;
  } catch {
    return false;
  }
}

/**
 * Refresh tokens are single-use, and presenting a spent one signs the user out
 * everywhere (the server treats it as theft). With several tabs open, another
 * tab may already have refreshed, so this runs one tab at a time and first
 * picks up anything newer that another tab saved.
 */
export async function performRefresh() {
  const run = async () => {
    const store = useAuthStore.getState();
    const saved = savedSession();
    let refreshToken = store.refreshToken;
    if (saved?.refreshToken && saved.refreshToken !== refreshToken && sameSession(saved, store)) {
      if (saved.accessToken && isTokenFresh(saved.accessToken)) {
        store.updateTokens(saved);
        return saved.accessToken;
      }
      refreshToken = saved.refreshToken;
    }
    if (!refreshToken) throw new Error('No refresh token');
    // Use a bare axios call so this request isn't itself intercepted/looped.
    const { data } = await axios.post(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken },
      // Never hold the cross-tab lock forever on a hung request.
      { timeout: 15_000 },
    );
    useAuthStore.getState().updateTokens(data);
    return data.accessToken;
  };
  return typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request('mdu-token-refresh', run)
    : run();
}

// When another tab refreshes, take its new tokens so this tab never presents
// a spent one. Only the tokens: each tab keeps its own workspace.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const next = JSON.parse(event.newValue)?.state;
      const store = useAuthStore.getState();
      if (
        next?.refreshToken &&
        next.accessToken &&
        next.refreshToken !== store.refreshToken &&
        sameSession(next, store)
      ) {
        store.updateTokens(next);
      }
    } catch {
      // Ignore anything that isn't our saved session.
    }
  });
}

// A dead impersonated session falls back to the stashed admin session instead
// of nuking everything — the super admin never gets logged out by proxy.
function dropSession() {
  const store = useAuthStore.getState();
  if (store.impersonation) {
    store.endImpersonation();
  } else {
    store.clear();
  }
}

function blobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * Downloads ask for a Blob, so an error body arrives as a Blob too. Replace it
 * with the JSON inside so callers can show the server's message (for example a
 * file blocked by the malware scan) instead of a bare status code.
 */
export async function readBlobErrorBody(response) {
  if (
    typeof Blob === 'undefined' ||
    !(response?.data instanceof Blob) ||
    !response.data.type?.includes('json')
  ) {
    return;
  }
  try {
    response.data = JSON.parse(await blobText(response.data));
  } catch {
    // Leave the Blob as it is; callers fall back to their own message.
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;
    await readBlobErrorBody(response);
    if (!response || response.status !== 401 || config?._retry || config?.skipAuth) {
      return Promise.reject(error);
    }
    if (!useAuthStore.getState().refreshToken) {
      dropSession();
      return Promise.reject(error);
    }
    // Another request (or tab) already refreshed while this one was in
    // flight: retry with the current token instead of refreshing again.
    const current = useAuthStore.getState().accessToken;
    const sentWith = config?.headers?.Authorization?.replace(/^Bearer /, '');
    if (current && sentWith && current !== sentWith && isTokenFresh(current)) {
      config._retry = true;
      config.headers.Authorization = `Bearer ${current}`;
      return api(config);
    }
    try {
      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      config._retry = true;
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${newToken}`;
      return api(config);
    } catch (refreshError) {
      dropSession();
      return Promise.reject(refreshError);
    }
  },
);

/** Extract a human-readable message from an axios error. */
export function getErrorMessage(error, fallback = 'Something went wrong') {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message[0];
  if (typeof message === 'string') return message;
  return error?.message || fallback;
}

/** True when the backend refused with a 422 plan-limit error (seats/workspaces). */
export function isLimitReached(error) {
  return error?.response?.data?.code === 'LIMIT_REACHED';
}
